import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { DndSortableList } from 'src/components/DndSortableList';
import { Grid } from 'src/components/Grid';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { externalLinks } from 'src/externalLinks';
import { AssessmentSequence, Grouping } from 'src/models/assessment/_types';
import {
  createAssessmentSequence,
  deleteGroupItem,
  fetchAssessmentOptions,
  updateAssessmentSequence,
} from 'src/models/assessment/assessment';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';
import ScrollToFirstError from 'src/shared/ScrollToFirstError';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import * as yup from 'yup';

interface SequenceFormDialogProps {
  open: boolean;
  onClose: () => void;
  editingSequence?: AssessmentSequence | null;
  onSuccess?: () => void;
}

type FormValues = {
  name: string;
  items: Array<{
    id: Identifier;
    groupableId: number;
    groupableName: string;
    groupableType: 'Assessment';
    key?: Identifier;
    sortOrder: number;
  }>;
  thankYouPage?: string;
};

export const SequenceFormDialog = ({
  open,
  onClose,
  editingSequence,
  onSuccess,
}: SequenceFormDialogProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment.sequence',
  });
  const { t: tAssessment } = useTranslation('translation', {
    keyPrefix: 'assessment',
  });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const { t: tPreviewDialog } = useTranslation('translation', {
    keyPrefix: 'previewDialog',
  });

  const queryClient = useQueryClient();
  const [showThankYou, setShowThankYou] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { data: assessments } = useQuery({
    queryKey: ['assessment-options'],
    queryFn: () => fetchAssessmentOptions({ 'q[s]': undefined }),
    select: (data) =>
      data.map((a) => ({
        id: Number(a.id),
        label: a.name,
      })),
  });

  const curDomain = externalLinks.railsBase || window.location.origin;
  const publicLink = editingSequence?.id
    ? `${curDomain}/assessments/sequence/${editingSequence.id}`
    : '';
  const privateLink = editingSequence?.id
    ? `${curDomain}/dashboard/me/assessment/take/sequence/${editingSequence.id}`
    : '';

  const initialValues: FormValues = {
    name: editingSequence?.name || '',
    items:
      editingSequence?.groupingItems?.map((it, index) => ({
        id: index + 1,
        key: it.id,
        groupableId: it.groupableId,
        groupableName: it.groupableName,
        groupableType: 'Assessment' as const,
        sortOrder: it.sortOrder,
      })) || [],
    thankYouPage: editingSequence?.thankYouPage || '',
  };

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = {
        id: editingSequence?.id,
        name: values.name,
        groupingItems: values.items.map((item, index) => ({
          id: item.key,
          groupableId: item.groupableId,
          groupableName: item.groupableName,
          groupableType: 'Assessment' as const,
          sortOrder: index,
        })),
        thankYouPage: values.thankYouPage,
      } as Grouping;

      if (payload.id) {
        return await updateAssessmentSequence({
          sequenceId: payload.id,
          data: payload,
        });
      } else {
        return await createAssessmentSequence({ data: payload });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment-sequences'] });
      onClose();
      toast.success(t('savedSuccess'));
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.error(api.formatError(error));
    },
  });

  const deleteGroupItemMutation = useMutation({
    mutationFn: deleteGroupItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment-sequences'] });
    },
    onError: (error) => {
      toast.error(api.formatError(error));
    },
  });

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required(tCommon('form.required', { field: tCommon('labels.name') })),
    items: yup
      .array()
      .min(
        1,
        tCommon('form.atLeastOne', { item: tAssessment('one').toLowerCase() })
      ),
  });

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        if (!values.name || values.items.length === 0) return;
        await saveMutation.mutateAsync(values);
      }}
    >
      {({ values, setFieldValue, submitForm }) => (
        <>
          <Dialog
            open={open}
            onClose={onClose}
            title={editingSequence ? t('edit') : t('create')}
            maxWidth="md"
            fullWidth
            actions={[
              { label: tCommon('buttons.cancel'), onClick: onClose },
              {
                label: editingSequence ? t('update') : t('create'),
                onClick: submitForm,
                variant: 'contained',
                loading: saveMutation.isPending,
              },
            ]}
          >
            <Stack spacing={2}>
              <ScrollToFirstError toastErrors={true} scrollTo={false} />
              <FormTextField
                name="name"
                label={tCommon('labels.name')}
                autoFocus
                required
              />

              <BaseSelect
                options={assessments || []}
                value={null}
                blurOnSelect={true}
                valueOnly={false}
                virtualized
                label={t('addAssessmentToSequence')}
                onChange={(assessment) => {
                  if (!assessment) return;
                  setFieldValue('items', [
                    ...values.items,
                    {
                      id: 'new' + (values.items.length + 1),
                      groupableId: Number(assessment.id),
                      groupableName: assessment.label || '',
                      groupableType: 'Assessment' as const,
                      sortOrder: values.items.length,
                    },
                  ]);
                }}
              />

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('assessmentOrder')}
                  {values.items.length > 0 && t('clickAndDragToReorder')}*
                </Typography>

                {values.items.length > 0 ? (
                  <DndSortableList
                    items={values.items}
                    setItems={(newItems) => {
                      if (Array.isArray(newItems)) {
                        setFieldValue(
                          'items',
                          newItems.map((it, index) => ({
                            ...it,
                            sortOrder: index,
                          }))
                        );
                      } else {
                        setFieldValue(
                          'items',
                          newItems(values.items).map((it, index) => ({
                            ...it,
                            sortOrder: index,
                          }))
                        );
                      }
                    }}
                  >
                    {(item, { index }) => (
                      <ListItem
                        primary={item.groupableName}
                        leftAdornment={index + 1}
                        rightAdornment={
                          <IconButton
                            size="small"
                            disabled={deleteGroupItemMutation.isPending}
                            onClick={async () => {
                              item.key &&
                                (await deleteGroupItemMutation.mutateAsync(
                                  item.key
                                ));
                              setFieldValue(
                                'items',
                                values.items.filter((_, i) => i !== index)
                              );
                            }}
                          >
                            <Icon
                              icon={icon({ name: 'trash', style: 'solid' })}
                            />
                          </IconButton>
                        }
                      />
                    )}
                  </DndSortableList>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 2 }}
                  >
                    {t('noAssessmentsAddedYet')}
                  </Typography>
                )}
              </Box>

              {showThankYou && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('customizeThankYouPage')} ({tCommon('labels.advanced')})
                  </Typography>
                  <TextField
                    multiline
                    rows={6}
                    value={values.thankYouPage || ''}
                    onChange={(e) =>
                      setFieldValue('thankYouPage', e.target.value)
                    }
                  />
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <HtmlTypography>
                      {t('thankYouEditorWarning')}
                    </HtmlTypography>
                  </Alert>
                  {values.thankYouPage && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowPreview(true)}
                      sx={{ mt: 1 }}
                    >
                      {tAssessment('preview')}
                    </Button>
                  )}
                  <Box sx={{ mt: 4, borderTop: 1, borderColor: 'divider' }} />
                </Box>
              )}
              {!showThankYou && (
                <Button
                  variant="text"
                  startIcon={
                    <Icon
                      icon={icon({ name: 'chevron-down', style: 'solid' })}
                    />
                  }
                  onClick={() => setShowThankYou(true)}
                >
                  {t('showThankYouEditor')}
                </Button>
              )}
              {editingSequence?.id && (
                <Grid container spacing={3} sx={{ mt: 4 }}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {tAssessment('shareablePublicLink')}:
                    </Typography>
                    <Link
                      href={publicLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', wordBreak: 'break-all' }}
                    >
                      {publicLink}
                    </Link>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {tAssessment('shareablePrivateLink')}:
                    </Typography>
                    <Link
                      href={privateLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', wordBreak: 'break-all' }}
                    >
                      {privateLink}
                    </Link>
                  </Grid>
                </Grid>
              )}
            </Stack>
          </Dialog>

          <Dialog
            open={showPreview}
            onClose={() => setShowPreview(false)}
            title={t('thankYouPagePreview')}
            maxWidth="md"
            fullWidth
          >
            <HtmlTypography>{values.thankYouPage || ''}</HtmlTypography>
            <Button
              onClick={() => setShowPreview(false)}
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
            >
              {tPreviewDialog('dismiss')}
            </Button>
          </Dialog>
        </>
      )}
    </Formik>
  );
};
